import { CellModel, ColumnSource, renderers, RequestFilters, RowSource } from "@omegagrid/grid";
import { GridEditorAdapter, GridEditorOptions, RequestParamsOrder, RowData, ExportType } from "@reporting/rp-app-host";
import { Thread } from "@reporting/rp-types/etl";
import { Module } from "@reporting/rp-types/accounting";
import { tree } from "@omegagrid/core";
import { msg } from "@omegagrid/localize"
import rc from "@reporting/rp-app-host";
import { helpers } from "@reporting/rp-app-host";

const DEFAULT_FIELDS = ['id_thread', 'description', 'type', 'enabled', 'module', 'ord_num', 'sync', 'app_lock', 'date_created', 'who_created_dv'];
const DEFAULT_ORDER: RequestParamsOrder = [['description', 'asc'], ['ord_num', 'asc']];

export class ThreadsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	get etlClient() { return rc.ms('etl') }
	get accountingClient() { return rc.ms('accounting') }

	constructor() { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		const threadTypes = await this.etlClient.get<tree.TreeNodeSource>(`threadTypes`);
		const modules = (await this.accountingClient.get<Module[]>(`modules`))
			.filter(m => m.type == 'data');
		const moduleColors = modules.reduce<Record<string, string>>((acc, item) =>
			Object.assign(acc, {[item.module]: item.color}), {});

		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: 'red',

			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: 'yellow'
			}],
			filter: false
		}, {
			data: 'id_thread',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 80
		}, {
			data: 'enabled',
			header: msg('Enabled'),
			t: 'b',
			e: true,
			w: 80
		}, {
			data: 'description',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'type',
			header: msg('Type'),
			t: 's',
			content: threadTypes,
			e: true,
			w: 210
		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			z: '0',
			e: true,
			w: 80
		}, {
			data: 'module',
			header: msg('Module'),
			t: 's',
			content: modules.map(m => ({k: m.module, v: m.name, icon: {icon: 'cube', color: m.color}})),
			renderer: (div: HTMLDivElement, cell: CellModel) => {
				renderers.DropdownRenderer(div, cell);
				if (cell.hasValue) div.insertAdjacentHTML('afterbegin', `
					<og-fa-icon icon="cube" style="color: ${moduleColors[cell.value as string]}"></og-fa-icon>
				`);
				return div;
			},
			e: true,
			w: 100
		}, {
			data: 'sync',
			header: msg('Sync'),
			t: 'b',
			e: true,
			w: 80
		}, {
			data: 'app_lock',
			header: msg('Lock'),
			t: 'b',
			e: true,
			w: 130
		}, {
			data: 'who_created_dv',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		return (await this.etlClient.get<Thread[]>(`threads`, this.createRequestParams(filters, {
			fields: DEFAULT_FIELDS,
			order: DEFAULT_ORDER,
			count: this.pageSize
		}))).map(data => ({ data }));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: this.pageSize,
			refreshAfterSave: true,
			exportTypes : ['csv', 'xlsx'],
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		const result = await this.etlClient.post(`threads`, data);
	}

	async export(type: ExportType) {
		if (type == 'xlsx') return await super.export(type);
		await this.etlClient.download('exportThreads', {
			fields: DEFAULT_FIELDS,
			order: DEFAULT_ORDER,
			fileName: helpers.createExportFilename(type),
			csvParams: {header: true, delimiter: ',', encoding: 'utf-8'},
		});
	}

}
